import { Stack, Typography, Box, Dialog, DialogTitle, DialogContent, Button, useTheme } from "@mui/material";
import { PrimaryButton } from "@/components/common";
import { FormButtonGroup } from "@/components/form";
import { ReactNode, useState } from "react";
import { useFluxSignerContext } from "@/contexts/FluxSignerContext";
import { Security, MenuBook } from "@mui/icons-material";
import { useSessionContext } from "@/contexts/SessionContext";
import { FluxSignerInstallationSteps } from "./FluxSignerInstallationSteps";

interface FluxSignerCardProps {
  title: string;
  description: string;
  status?: string;
  children?: ReactNode;
  priceInfo?: string;
}

/**
 * Card para a extensão FluxSigner
 * Permite ativar/desativar a assinatura digital do computador
 */
export function FluxSignerCard({
  title,
  description,
  status,
  children,
  priceInfo,
}: FluxSignerCardProps) {
  const { access } = useSessionContext();
  const theme = useTheme();
  const { contractFluxSigner, activateFluxSigner, deactivateFluxSigner, isLoading } =
    useFluxSignerContext();
  
  const [openActivateDialog, setOpenActivateDialog] = useState(false);
  const [openDeactivateDialog, setOpenDeactivateDialog] = useState(false);
  const [openManualDialog, setOpenManualDialog] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [manualStep, setManualStep] = useState(1);

  const handleActivate = async () => {
    const success = await activateFluxSigner();
    if (success) {
      setOpenActivateDialog(false);
      setCurrentStep(1); // Reset para o step 1
    }
  };

  const handleDeactivate = async () => {
    setOpenDeactivateDialog(false);
    await deactivateFluxSigner();
  };

  const handleNextStep = () => {
    if (currentStep < 3) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePreviousStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleCloseActivateDialog = () => {
    setOpenActivateDialog(false);
    setCurrentStep(1); // Reset ao fechar
  };

  const handleNextManualStep = () => {
    if (manualStep < 2) {
      setManualStep(manualStep + 1);
    }
  };

  const handlePreviousManualStep = () => {
    if (manualStep > 1) {
      setManualStep(manualStep - 1);
    }
  };

  const handleCloseManualDialog = () => {
    setOpenManualDialog(false);
    setManualStep(1); // Reset ao fechar
  };

  return (
    <Stack
      sx={{
        border: "1px solid",
        borderColor: "neutral700.main",
        borderRadius: "15px",
        position: "relative",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        height: "100%",
        minHeight: "400px",
        paddingTop: 4,
        paddingBottom: 3,
      }}
    >
      {/* Badge de preço */}
      {priceInfo && (
        <Stack
          sx={{
            position: "absolute",
            top: "12px",
            left: "0px",
            bgcolor: "secondary800.main",
            color: "white",
            padding: "4px 12px",
            borderTopRightRadius: "15px",
            borderBottomRightRadius: "15px",
            fontSize: "12px",
            fontWeight: "bold",
            zIndex: 1,
          }}
        >
          {priceInfo}
        </Stack>
      )}

      {/* Badge de status */}
      {status && (
        <Stack
          sx={{
            position: "absolute",
            top: "12px",
            right: "0px",
            bgcolor:
              status === "Ativo"
                ? "success.main"
                : status === "Disponível"
                ? "success.main"
                : status === "Em desenvolvimento"
                ? "warning.main"
                : "info.main",
            color: "white",
            padding: "4px 12px",
            borderTopLeftRadius: "15px",
            borderBottomLeftRadius: "15px",
            fontSize: "12px",
            fontWeight: "bold",
            zIndex: 1,
          }}
        >
          {status}
        </Stack>
      )}

      {/* Título */}
      <Stack
        direction="row"
        width="100%"
        justifyContent="center"
        marginTop="auto"
        paddingTop={2}
      >
        <Typography
          sx={{
            fontSize: "1.25rem",
            fontWeight: "500",
            paddingBottom: 2,
            paddingLeft: 5,
            paddingRight: 5,
            paddingTop: 1,
            alignSelf: "center",
            textAlign: "center",
            position: "relative",
            zIndex: 0,
          }}
        >
          {title}
        </Typography>
      </Stack>

      {/* Descrição */}
      <Stack flexGrow={1} overflow="auto" maxHeight="330px">
        <Typography
          variant="body1"
          color="neutral700.main"
          sx={{
            paddingBottom: 3,
            paddingLeft: 5,
            paddingRight: 5,
          }}
          align="left"
        >
          {description}
        </Typography>

        {children && (
          <Stack paddingLeft={5} paddingRight={5} paddingBottom={3}>
            {children}
          </Stack>
        )}

        {/* Informações adicionais */}
        <Box paddingLeft={5} paddingRight={5}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            <strong>Recursos inclusos:</strong>
          </Typography>
          <ul style={{ marginTop: 8, paddingLeft: 20 }}>
            <li>
              <Typography variant="body2" color="text.secondary">
                Assinatura digital com certificados ICP-Brasil
              </Typography>
            </li>
            <li>
              <Typography variant="body2" color="text.secondary">
                Suporte a certificados A1 e A3
              </Typography>
            </li>
            <li>
              <Typography variant="body2" color="text.secondary">
                Assinatura rápida e segura direto do navegador
              </Typography>
            </li>
          </ul>

          {/* Link para o manual de instalação */}
          <Button
            onClick={() => setOpenManualDialog(true)}
            startIcon={<MenuBook />}
            sx={{ mt: 2, textTransform: "none" }}
            size="small"
          >
            Manual de instalação
          </Button>
        </Box>
      </Stack>

      {/* Botões de ação */}
      <Stack
        direction="row"
        width="100%"
        justifyContent="center"
        marginTop="auto"
        paddingTop={2}
      >
        {contractFluxSigner ? (
          <Button
            sx={{
              width: "70%",
              height: "40px",
              backgroundColor: access?.roles?.includes("owner")
                ? "error700.main"
                : "neutral300.main",
              color: "neutral0.main",
              borderRadius: "8px",
              "&:hover": {
                backgroundColor: access?.roles?.includes("owner")
                  ? "error700.main"
                  : "neutral300.main",
              },
              textTransform: "capitalize",
              fontSize: theme.typography.subtitle2.fontSize,
              "&:disabled": {
                color: "neutral600.main",
              },
            }}
            startIcon={<span style={{ color: "white" }}>⚠️</span>}
            disabled={!access?.roles?.includes("owner")}
            onClick={() => setOpenDeactivateDialog(true)}
          >
            {access?.roles?.includes("owner")
              ? "Cancelar FluxSigner"
              : "Somente proprietários"}
          </Button>
        ) : (
          <PrimaryButton
            width="70%"
            height="40px"
            onClick={() => setOpenActivateDialog(true)}
            startIcon={<Security />}
            disabled={!access?.roles?.includes("owner")}
          >
            {access?.roles?.includes("owner")
              ? "Ativar FluxSigner"
              : "Somente proprietários"}
          </PrimaryButton>
        )}
      </Stack>

      {/* Modal de ativação com steps */}
      <Dialog
        open={openActivateDialog}
        onClose={handleCloseActivateDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "10px",
            padding: 2,
          },
        }}
      >
        <DialogTitle textAlign="center">
          <Typography variant="h5" fontWeight={600}>
            Ativar FluxSigner - Passo {currentStep} de 3
          </Typography>
        </DialogTitle>
        <DialogContent>
          {/* Usar o componente de steps de instalação */}
          <FluxSignerInstallationSteps currentStep={currentStep} />

          {/* FormButtonGroup para navegação entre steps */}
          <Box mt={3}>
            <FormButtonGroup
              goBackButtonText={currentStep === 1 ? "Cancelar" : "Voltar"}
              nextButtonText={currentStep === 3 ? "Finalizar" : "Próximo"}
              onGoBackButtonClick={
                currentStep === 1
                  ? handleCloseActivateDialog
                  : handlePreviousStep
              }
              onNextButtonClick={
                currentStep === 3 ? handleActivate : handleNextStep
              }
              loading={isLoading}
              disabled={isLoading}
            />
          </Box>
        </DialogContent>
      </Dialog>

      {/* Modal de confirmação de desativação */}
      <Dialog
        open={openDeactivateDialog}
        onClose={() => setOpenDeactivateDialog(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "10px",
            padding: 2,
          },
        }}
      >
        <DialogTitle textAlign="center">
          <Typography variant="h5" fontWeight={600}>
            Confirmar cancelamento da extensão FluxSigner
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography
            variant="subtitle2"
            fontWeight="500"
            align="center"
            mt={2}
          >
            Após a confirmação, o FluxSigner será desativado e os recursos
            adicionais não estarão mais disponíveis.
          </Typography>

          <Box mt={3}>
            <FormButtonGroup
              goBackButtonText="Cancelar"
              nextButtonText="Confirmar"
              onGoBackButtonClick={() => setOpenDeactivateDialog(false)}
              onNextButtonClick={handleDeactivate}
              loading={isLoading}
              disabled={isLoading}
            />
          </Box>
        </DialogContent>
      </Dialog>

      {/* Modal do Manual de Instalação */}
      <Dialog
        open={openManualDialog}
        onClose={handleCloseManualDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "10px",
            padding: 2,
          },
        }}
      >
        <DialogTitle textAlign="center">
          <Typography variant="h5" fontWeight={600}>
            Manual de Instalação FluxSigner - Passo {manualStep} de 2
          </Typography>
        </DialogTitle>
        <DialogContent>
          {/* Usar o componente de steps de instalação (apenas passos 1 e 2) */}
          <FluxSignerInstallationSteps currentStep={manualStep} />

          {/* FormButtonGroup para navegação entre steps do manual */}
          <Box mt={3}>
            <FormButtonGroup
              goBackButtonText={manualStep === 1 ? "Fechar" : "Voltar"}
              nextButtonText={manualStep === 2 ? "Concluir" : "Próximo"}
              onGoBackButtonClick={manualStep === 1 ? handleCloseManualDialog : handlePreviousManualStep}
              onNextButtonClick={manualStep === 2 ? handleCloseManualDialog : handleNextManualStep}
            />
          </Box>
        </DialogContent>
      </Dialog>
    </Stack>
  );
}

